import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Breadcrumbs, Link } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import NotFound from '../NotFound';
import StaffAppBar from '../StaffAppBar';
import ProfileShell from '../profile/ProfileShell';
import OverviewTab from './OverviewTab';
import PermissionsTab from './PermissionsTab';
import SecurityTab from './SecurityTab';
import { getUserDetail, ForbiddenError } from '../../services/userDetailService';

const VIEW_PERMISSION = 'can_view_user_details';
const MANAGE_MFA_PERMISSION = 'can_manage_user_mfa';

const BASE_TABS = ['Overview', 'Permissions'];

/**
 * Staff-facing detail page for one user.
 *
 * The permission check here is defence in depth, not the security control:
 * userPermissions lives in localStorage and the operator can edit it. The
 * control is the stacked middleware on GET /admin/users/:id. The two meet at
 * the same place — a ForbiddenError from the API renders the same 404 the local
 * check does — so an edited localStorage buys nothing but an extra round trip.
 *
 * The layout lives in ProfileShell, shared with the self-service Account page.
 * This page keeps its own data path: the admin detail response carries fields
 * (ssn_last_four, login_count, role provenance) that GET /user does not, and
 * merging the two would mean one component guessing which endpoint it was
 * looking at. No adapter is needed here because the canonical profile shape the
 * shell consumes is this endpoint's own shape; Account is the one that renames.
 *
 * The Security tab here is the staff break-glass path and shares nothing with
 * the account page's tab of the same name. That one is self-service: it acts on
 * the bearer token's own user and demands a password and a current code. This
 * one names a user in the path and demands neither. They are separate
 * components against separate endpoints precisely so neither can be mistaken
 * for the other — the self-service settings can never appear on a page showing
 * somebody else's profile, because that component is not imported here.
 */
function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const permissions = JSON.parse(localStorage.getItem('userPermissions') || '[]');
  const mayView = permissions.includes(VIEW_PERMISSION);
  // Same defence-in-depth as mayView: the server enforces can_manage_user_mfa
  // on the endpoint. Hiding the tab keeps an operator who cannot use it from
  // being offered it, rather than standing in for the check.
  const mayManageMfa = permissions.includes(MANAGE_MFA_PERMISSION);
  const tabs = mayManageMfa ? [...BASE_TABS, 'Security'] : BASE_TABS;

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setForbidden(false);
      // getUserDetail either resolves with data or throws — there is no
      // longer a third, silent "returned undefined" case to guard against.
      const data = await getUserDetail(id);
      setDetail(data);
    } catch (err) {
      if (err instanceof ForbiddenError) {
        setForbidden(true);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!mayView) return;
    fetchDetail();
  }, [mayView, fetchDetail]);

  if (!mayView || forbidden) {
    return <NotFound />;
  }

  const displayName = detail
    ? [detail.first_name, detail.last_name].filter(Boolean).join(' ')
    : '';

  return (
    <ProfileShell
      profile={detail}
      loading={loading}
      error={error}
      tabs={tabs}
      idPrefix="user-detail"
      tabsAriaLabel="User detail sections"
      activeTab={activeTab}
      onTabChange={setActiveTab}
      appBar={<StaffAppBar />}
      // A breadcrumb rather than Account's plain title: this page is reachable
      // only from the admin dashboard, so the crumb names where the reader
      // actually came from.
      header={
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate('/admin/dashboard')}
          >
            Admin Dashboard
          </Link>
          <Typography color="text.primary">{displayName || 'User'}</Typography>
        </Breadcrumbs>
      }
    >
      {activeTab === 0 && <OverviewTab detail={detail} />}
      {activeTab === 1 && (
        <PermissionsTab detail={detail} onRolesChanged={fetchDetail} />
      )}
      {/* Re-fetch after a clear so the tab stops reporting an enrollment that
          is no longer there — the same rule the Permissions tab follows. */}
      {activeTab === 2 && mayManageMfa && (
        <SecurityTab detail={detail} onCleared={fetchDetail} />
      )}
    </ProfileShell>
  );
}

export default UserDetail;
